import React, { useState } from 'react';
import Button from './Button';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const DuplicateSongCleanupModal = ({
  isOpen,
  onClose,
  onCleanup,
  title = "Remove Duplicate Songs"
}) => {
  const { user } = useAuth();
  const [similarityThreshold, setSimilarityThreshold] = useState(0.85);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    setPreviewData(null);
    
    try {
      const result = await onCleanup({
        similarityThreshold,
        dryRun: true
      });
      
      if (result?.error) {
        setError(result?.error);
      } else {
        setPreviewData(result);
      }
    } catch (err) {
      setError('Preview failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!previewData) {
      setError('Please run preview first');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await onCleanup({
        similarityThreshold,
        dryRun: false
      });
      
      if (result?.error) {
        setError(result?.error);
      } else {
        setResults(result);
        setPreviewData(null); // Clear preview after execution
      }
    } catch (err) {
      setError('Cleanup failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPreviewData(null);
    setResults(null);
    setError(null);
    setLoading(false);
    setSimilarityThreshold(0.85);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-semibold text-gray-900">
              {title}
            </h2>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Icon name="X" size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Warning */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-yellow-900 mb-1">
                  Duplicate Song Removal
                </h3>
                <p className="text-sm text-yellow-800">
                  This will identify and remove duplicate songs based on title similarity and musical context. 
                  The most recently created song in each group will be kept. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Detection Settings
            </h3>
            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Similarity Threshold ({similarityThreshold})
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="1.0"
                  step="0.05"
                  value={similarityThreshold}
                  onChange={(e) => setSimilarityThreshold(parseFloat(e?.target?.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  disabled={loading}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Loose matching (0.5)</span>
                  <span>Exact matching (1.0)</span>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Higher values = more strict matching. Recommended: 0.85 for good balance.
                </p>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          {!results && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Preview Changes
                </h3>
                <Button
                  variant="outline"
                  onClick={handlePreview}
                  iconName="Eye"
                  iconPosition="left"
                  disabled={loading}
                  className="rounded-lg"
                >
                  {loading ? 'Analyzing...' : 'Preview'}
                </Button>
              </div>

              {loading && (
                <div className="text-center py-8">
                  <Icon name="Loader2" size={32} className="animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Analyzing songs for duplicates...</p>
                </div>
              )}

              {previewData && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Preview Results</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="text-blue-900 font-medium">Duplicate Groups</div>
                        <div className="text-blue-700 text-lg font-semibold">
                          {previewData?.summary?.groups_processed || 0}
                        </div>
                      </div>
                      <div className="bg-red-50 p-3 rounded-lg">
                        <div className="text-red-900 font-medium">Songs to Remove</div>
                        <div className="text-red-700 text-lg font-semibold">
                          {previewData?.summary?.duplicates_removed || 0}
                        </div>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="text-green-900 font-medium">Songs to Keep</div>
                        <div className="text-green-700 text-lg font-semibold">
                          {previewData?.summary?.songs_kept || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {previewData?.summary?.summary && previewData?.summary?.summary?.length > 0 && (
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Details</h5>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {previewData?.summary?.summary?.map((group, index) => (
                          <div key={index} className="text-sm bg-gray-50 p-3 rounded border">
                            <div className="font-medium">Group {group?.group_id}</div>
                            <div className="text-gray-600">
                              Will keep 1 song, remove {group?.removed_count} duplicate{group?.removed_count !== 1 ? 's' : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Results Section */}
          {results && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Cleanup Results
              </h3>
              
              <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-green-900 mb-1">
                      Cleanup Completed Successfully
                    </h4>
                    <p className="text-sm text-green-800 mb-3">
                      {results?.message || 'Duplicate removal completed'}
                    </p>
                    
                    {results?.summary && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                        <div className="bg-white bg-opacity-50 p-3 rounded">
                          <div className="text-green-900 font-medium">Groups Processed</div>
                          <div className="text-green-700 font-semibold">
                            {results?.summary?.groups_processed || 0}
                          </div>
                        </div>
                        <div className="bg-white bg-opacity-50 p-3 rounded">
                          <div className="text-green-900 font-medium">Songs Removed</div>
                          <div className="text-green-700 font-semibold">
                            {results?.summary?.duplicates_removed || 0}
                          </div>
                        </div>
                        <div className="bg-white bg-opacity-50 p-3 rounded">
                          <div className="text-green-900 font-medium">Songs Kept</div>
                          <div className="text-green-700 font-semibold">
                            {results?.summary?.songs_kept || 0}
                          </div>
                        </div>
                      </div>
                    )}

                    {results?.summary?.errors && results?.summary?.errors?.length > 0 && (
                      <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                        <div className="text-sm text-red-800">
                          <strong>Some errors occurred:</strong>
                          <ul className="mt-1 list-disc list-inside">
                            {results?.summary?.errors?.slice(0, 3)?.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                            {results?.summary?.errors?.length > 3 && (
                              <li>... and {results?.summary?.errors?.length - 3} more</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-red-900 mb-1">Error</h4>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 rounded-lg"
            >
              {results ? 'Done' : 'Cancel'}
            </Button>

            {!results && previewData && (
              <Button
                variant="primary"
                onClick={handleExecute}
                iconName="Trash2"
                iconPosition="left"
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 rounded-lg"
              >
                {loading ? 'Removing...' : 'Execute Cleanup'}
              </Button>
            )}
          </div>

          {/* Info Footer */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600">
              <strong>How it works:</strong> Songs are grouped by title similarity and musical context. 
              Within each group, the most recently created song is kept and others are removed. 
              All related data (favorites, videos, roles) is also cleaned up.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateSongCleanupModal;